import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Calendar, HardDrive, Filter, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/services/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface RepositoryDocument {
  id: number;
  filename: string;
  file_type: string;
  file_size: number;
  categories: string[];
  framework_relevance: Record<string, { score: number; reason: string }>;
  processed: boolean;
  uploaded_at: string;
  last_used_at: string | null;
}

interface RepositoryDocumentListProps {
  refreshTrigger?: number;
}

export function RepositoryDocumentList({ refreshTrigger }: RepositoryDocumentListProps) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<RepositoryDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState<string>('all');
  const [selectedDoc, setSelectedDoc] = useState<RepositoryDocument | null>(null);
  const [docDetails, setDocDetails] = useState<any>(null);

  const frameworks = ['all', 'DORA', 'ISO_27001', 'SOC2', 'ESG', 'RFP', 'QUESTIONNAIRE', 'SERVICE_PROPOSAL'];

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = selectedFramework !== 'all' ? { framework: selectedFramework } : {};
      const response = await api.get('/repository/documents', { params });
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error(t('repository.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [selectedFramework, refreshTrigger]);

  const handleDelete = async (docId: number) => {
    if (!confirm(t('repository.deleteConfirm'))) return;

    try {
      await api.delete(`/repository/documents/${docId}`);
      toast.success(t('repository.documentDeleted'));
      loadDocuments();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(t('repository.deleteFailed'));
    }
  };

  const handleViewDetails = async (doc: RepositoryDocument) => {
    try {
      const response = await api.get(`/repository/documents/${doc.id}`, {
        params: { include_extractions: true }
      });
      setDocDetails(response.data);
      setSelectedDoc(doc);
    } catch (error) {
      console.error('Failed to load details:', error);
      toast.error(t('repository.detailsLoadFailed'));
    }
  };

  const getFrameworkColor = (framework: string) => {
    const colors: Record<string, string> = {
      DORA: 'bg-blue-500',
      ISO_27001: 'bg-green-500',
      SOC2: 'bg-purple-500',
      ESG: 'bg-emerald-500',
      RFP: 'bg-orange-500',
      QUESTIONNAIRE: 'bg-pink-500',
      SERVICE_PROPOSAL: 'bg-teal-500',
    };
    return colors[framework] || 'bg-gray-500';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Filter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('repository.title')}</CardTitle>
              <CardDescription>
                {selectedFramework !== 'all'
                  ? t('repository.documentsAvailableFor', { count: documents.length, framework: selectedFramework })
                  : t('repository.documentsAvailable', { count: documents.length })}
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={selectedFramework} onValueChange={setSelectedFramework}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('repository.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('repository.allCategories')}</SelectItem>
                  {frameworks.slice(1).map((fw) => (
                    <SelectItem key={fw} value={fw}>
                      {fw}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Document List */}
      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">{t('repository.loading')}</p>
            </CardContent>
          </Card>
        ) : documents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {selectedFramework !== 'all'
                  ? t('repository.noDocumentsForFramework', { framework: selectedFramework })
                  : t('repository.noDocuments')}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {t('repository.uploadToGetStarted')}
              </p>
            </CardContent>
          </Card>
        ) : (
          documents.map((doc) => (
            <Card key={doc.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  {/* Document Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium">{doc.filename}</p>
                        <p className="text-xs text-gray-500">
                          {doc.file_type.toUpperCase()} • {formatFileSize(doc.file_size)}
                        </p>
                      </div>
                    </div>

                    {/* Framework Tags */}
                    {doc.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {doc.categories.map((cat) => (
                          <Badge
                            key={cat}
                            className={`${getFrameworkColor(cat)} text-white text-xs`}
                          >
                            {cat}
                            {doc.framework_relevance[cat] && (
                              <span className="ml-1">
                                ({doc.framework_relevance[cat].score}/10)
                              </span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {doc.uploaded_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>
                            Uploaded {formatDistanceToNow(new Date(doc.uploaded_at), { addSuffix: true })}
                          </span>
                        </div>
                      )}
                      {doc.last_used_at && (
                        <div className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          <span>
                            Last used {formatDistanceToNow(new Date(doc.last_used_at), { addSuffix: true })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(doc)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{selectedDoc?.filename}</DialogTitle>
                          <DialogDescription>{t('repository.documentDetails')}</DialogDescription>
                        </DialogHeader>
                        
                        {docDetails && (
                          <div className="space-y-4">
                            {/* Basic Info */}
                            <div>
                              <h4 className="text-sm font-semibold mb-2">{t('repository.basicInfo')}</h4>
                              <div className="space-y-1 text-sm">
                                <p><span className="font-medium">{t('repository.fileType')}:</span> {docDetails.file_type}</p>
                                <p><span className="font-medium">{t('repository.size')}:</span> {formatFileSize(docDetails.file_size)}</p>
                                <p><span className="font-medium">{t('repository.processed')}:</span> {docDetails.processed ? t('repository.yes') : t('repository.no')}</p>
                              </div>
                            </div>

                            {/* Framework Relevance */}
                            <div>
                              <h4 className="text-sm font-semibold mb-2">{t('repository.frameworkRelevance')}</h4>
                              <div className="space-y-2">
                                {Object.entries(docDetails.framework_relevance || {}).map(([fw, data]: [string, any]) => (
                                  <div key={fw} className="text-sm">
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium">{fw}</span>
                                      <Badge variant={data.score >= 5 ? 'default' : 'secondary'}>
                                        {data.score}/10
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">{data.reason}</p>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Extractions */}
                            {docDetails.extractions && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2">{t('repository.preExtractedData')}</h4>
                                <div className="space-y-2">
                                  {Object.entries(docDetails.extractions).map(([framework, extraction]) => {
                                    if (!extraction) return null;
                                    return (
                                      <details key={framework} className="text-sm">
                                        <summary className="font-medium cursor-pointer hover:text-blue-600">
                                          {framework.toUpperCase()} {t('repository.extraction')}
                                        </summary>
                                        <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                                          {JSON.stringify(extraction, null, 2)}
                                        </pre>
                                      </details>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Text Preview */}
                            {docDetails.raw_text_preview && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2">{t('repository.textPreview')}</h4>
                                <p className="text-xs text-gray-600 bg-gray-50 p-3 rounded">
                                  {docDetails.raw_text_preview}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(doc.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
